/**
 * O que faz: Permite entrar no Gens CRM com e-mail/senha ou Google.
 * Onde usa: Rota pública /login.
 * Conexões: AuthContext (signIn, signInWithGoogle), React Router e TelaLoginLayout.
 * Tabelas: Nenhuma nesta tela; a autenticação passa pelo Supabase Auth.
 * Data: 19/09/2026.
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ThinkingOrbLoader } from '@/components/ui/ThinkingOrbLoader';
import { TelaLoginLayout } from '@/components/auth/TelaLoginLayout';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { SimboloGens } from "@/components/brand/MarcaGens";

function IconeGoogle() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

export const SignInPage = () => {
  const {
    signIn,
    signInWithGoogle,
    loading,
    user,
    initialAuthChecked
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Estado para controlar loading do botão Google
  const [googleLoading, setGoogleLoading] = useState(false);

  // Obter a página de origem para redirecionamento após login
  const from = location.state?.from?.pathname || '/dashboard';

  // Redireciona automaticamente se o usuário já estiver autenticado
  // Isso é essencial para o fluxo OAuth (Google) funcionar corretamente
  useEffect(() => {
    if (initialAuthChecked && user) {
      console.log('[SignInPage] Usuário já autenticado, redirecionando para:', from);
      navigate(from, { replace: true });
    }
  }, [user, initialAuthChecked, navigate, from]);

  // Estados do formulário
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Estados de UI
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Atualizar campos do formulário
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpar erro do campo quando usuário começa a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Validar email
  const isValidEmail = (email: string) => {
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(email);
  };

  // Validar formulário
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Email deve ter formato válido';
    }
    if (!formData.password) {
      newErrors.password = 'Senha é obrigatória';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submeter formulário
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Verifique os campos do formulário');
      return;
    }
    try {
      await signIn(formData.email.trim(), formData.password);

      // Redirecionar para a página de origem ou dashboard
      navigate(from, {
        replace: true
      });
    } catch (error) {
      // Erro já é tratado no contexto de autenticação
      console.error('Erro no login:', error);
    }
  };

  // Handler para login com Google
  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true);
      await signInWithGoogle();
      // O redirecionamento acontece automaticamente pelo Supabase OAuth
    } catch (error) {
      console.error('Erro no login com Google:', error);
      setGoogleLoading(false);
    }
  };

  const formularioBloqueado = loading || googleLoading;

  // Evita flash do formulário de login para quem já está logado
  if (loading || !initialAuthChecked) {
    return (
      <TelaLoginLayout>
        <div className="login-gens-carregando">
          <ThinkingOrbLoader state="solving" size={64} label="Verificando autenticação..." />
        </div>
      </TelaLoginLayout>
    );
  }

  return (
    <TelaLoginLayout>
      <Link to="/" className="login-gens-brand" aria-label="Gens CRM — início">
        <span aria-hidden="true"><SimboloGens className="h-[58%] w-auto" /></span>
        gens
        <span className="login-gens-brand-suffix">CRM</span>
      </Link>

      <h1 className="login-gens-title">
        Boas vindas ao
        <span>Gens CRM</span>
      </h1>
      <p className="login-gens-lead">
        Organize o atendimento da sua clínica. Sem planilha, sem conversa perdida.
      </p>

      <form onSubmit={handleSubmit} className="login-gens-acoes" noValidate>
        <button
          type="button"
          className="login-gens-google"
          onClick={handleGoogleSignIn}
          disabled={formularioBloqueado}
        >
          <IconeGoogle />
          {googleLoading ? 'Conectando...' : 'Entrar com Google'}
        </button>

        <p className="login-gens-divisor">Ou entre com e-mail</p>

        <div className="login-gens-campo">
          <label htmlFor="email">Email de acesso</label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={e => handleInputChange('email', e.target.value)}
            placeholder="voce@clinica.com"
            className={`login-gens-input ring-0 focus-visible:ring-0 ${errors.email ? 'login-gens-input-erro' : ''}`}
            required
            autoComplete="email"
          />
          {errors.email && <p className="login-gens-erro">{errors.email}</p>}
        </div>

        <div className="login-gens-campo">
          <div className="login-gens-campo-topo">
            <label htmlFor="password">Senha</label>
            <Link to="/esqueci-senha" className="login-gens-esqueci">
              Esqueceu sua senha?
            </Link>
          </div>
          <div className="login-gens-senha">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={e => handleInputChange('password', e.target.value)}
              placeholder="Digite sua senha"
              className={`login-gens-input ring-0 focus-visible:ring-0 ${errors.password ? 'login-gens-input-erro' : ''}`}
              required
              autoComplete="current-password"
            />
            <button
              type="button"
              className="login-gens-olho"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && <p className="login-gens-erro">{errors.password}</p>}
        </div>

        <button type="submit" className="login-gens-enviar" disabled={formularioBloqueado}>
          {loading ? 'Entrando...' : 'Entrar'}
        </button>
      </form>

      <p className="login-gens-rodape">
        Ao entrar, você concorda com nossa{' '}
        <Link to="/politica-de-privacidade">política de privacidade</Link>.
      </p>

      <p className="login-gens-criar">
        Não tem uma conta?{' '}
        <Link to="/signup">Criar conta</Link>
      </p>

      <Link to="/" className="login-gens-voltar">
        <ArrowLeft className="w-4 h-4" />
        Voltar ao início
      </Link>
    </TelaLoginLayout>
  );
};

export default SignInPage;
